import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

import {
  calculateProgressFill,
  getBackgroundColor,
  getColorFiller,
  getSeparatorPosition,
  Steps,
} from './Tracker.helpers';
import TrackerTexture, {
  TrackerTextureSourceType,
} from './TrackerTexture/TrackerTexture';

interface BaseTrackerProps {
  fillColor?: Maybe<string>;
  backgroundTexture?: Maybe<TrackerTextureSourceType>;
  backgroundColor?: string;
  hasAnimation: boolean;
}

export interface SimpleTrackerProps extends BaseTrackerProps {
  trackerType: 'SIMPLE';
  progressValue: number;
}

export interface ComplexTrackerProps extends BaseTrackerProps {
  trackerType: 'COMPLEX';
  steps: Steps[];
  fillToStep: number;
}

export type TrackerProps = SimpleTrackerProps | ComplexTrackerProps;

const Tracker = ({
  trackerType,
  backgroundTexture,
  fillColor,
  hasAnimation,
  backgroundColor,
  ...props
}: TrackerProps) => {
  let cumulativeLength = 0;
  const isSimpleTracker = trackerType === 'SIMPLE';
  const fillColorStyle = getColorFiller(fillColor);
  const componentBackgroundColor = getBackgroundColor(
    isSimpleTracker,
    backgroundColor,
  );

  const progressValue = isSimpleTracker
    ? (props as SimpleTrackerProps).progressValue
    : 0;
  const steps = !isSimpleTracker
    ? (props as ComplexTrackerProps).steps
    : undefined;
  const fillToStep = !isSimpleTracker
    ? (props as ComplexTrackerProps).fillToStep
    : 0;

  const totalLength = steps?.reduce((acc, step) => acc + step.length, 0) ?? 0;
  const separators = steps?.slice(0, -1).map((step) => {
    cumulativeLength += step.length;
    return Math.round((cumulativeLength / totalLength) * 100);
  });

  const percentageFill = calculateProgressFill(
    fillToStep,
    trackerType,
    progressValue,
    steps ?? [],
    totalLength,
  );
  const animatedFill = useSharedValue(0);

  useEffect(() => {
    if (hasAnimation) {
      animatedFill.value = withTiming(percentageFill, {
        duration: 1000,
        easing: Easing.linear,
      });
    } else {
      animatedFill.value = percentageFill;
    }
  }, [hasAnimation, percentageFill, animatedFill]);

  const animatedStyle = useAnimatedStyle(() => ({
    width: `${100 - animatedFill.value}%`,
    alignSelf: 'flex-end',
  }));

  return (
    <View
      style={[
        styles.trackerContainer,
        componentBackgroundColor,
        percentageFill < 100 && styles.noPaddingRight,
      ]}
      testID="trackerContainer"
    >
      {isSimpleTracker ? (
        <>
          {backgroundTexture ? (
            <TrackerTexture
              backgroundTexture={backgroundTexture}
              testID="backgroundImageSimple"
              style={styles.imageFill}
            />
          ) : (
            <View style={[styles.colorFill, fillColorStyle.fillColor]} />
          )}
          <Animated.View
            style={[
              styles.mask,
              percentageFill <= 100 && styles.noLeftRadius,
              animatedStyle,
              componentBackgroundColor,
            ]}
            testID="mask-simple"
          >
            {percentageFill < 100 && percentageFill > 0 ? (
              <View
                style={[styles.clip, fillColorStyle.fillColor]}
                testID="clip"
              >
                {backgroundTexture ? (
                  <TrackerTexture
                    backgroundTexture={backgroundTexture}
                    testID="clipBg"
                    style={styles.clipBg}
                  />
                ) : null}
              </View>
            ) : null}
          </Animated.View>
        </>
      ) : (
        <>
          {backgroundTexture ? (
            <TrackerTexture
              backgroundTexture={backgroundTexture}
              testID="backgroundImageComplex"
              style={styles.imageFill}
            />
          ) : (
            <View style={[styles.colorFill, fillColorStyle.fillColor]} />
          )}
          <Animated.View
            style={[
              styles.mask,
              styles.noLeftRadius,
              animatedStyle,
              componentBackgroundColor,
            ]}
            testID="mask-complex"
          />
        </>
      )}
      {!isSimpleTracker
        ? separators?.map((separatorPosition) => {
            const separatorPositionStyle =
              getSeparatorPosition(separatorPosition);
            return (
              <View
                key={separatorPosition}
                style={[
                  styles.separatorStyle,
                  separatorPositionStyle.separatorPosition,
                ]}
                testID="separators"
              />
            );
          })
        : null}
    </View>
  );
};

const styles = StyleSheet.create({
  clip: {
    borderBottomRightRadius: 10,
    borderTopRightRadius: 10,
    bottom: 0,
    position: 'absolute',
    top: 0,
    width: 6,
  },
  clipBg: {
    borderBottomRightRadius: 10,
    borderTopRightRadius: 10,
    height: '100%',
    overflow: 'hidden',
    width: '100%',
  },
  colorFill: {
    borderRadius: 10,
    height: '100%',
    overflow: 'hidden',
  },
  imageFill: {
    borderRadius: 10,
    height: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    width: '100%',
  },
  mask: {
    borderRadius: 10,
    bottom: 12,
    height: '100%',
    zIndex: 1,
  },
  noLeftRadius: {
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
  },
  noPaddingRight: {
    paddingRight: 0,
  },
  separatorStyle: {
    backgroundColor: theme.color.background.primary,
    bottom: 0,
    position: 'absolute',
    top: 0,
    width: 4,
    zIndex: 3,
  },
  trackerContainer: {
    borderRadius: 10,
    height: 20,
    overflow: 'hidden',
    padding: theme.spacing.xs,
  },
});

export default Tracker;
